import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { LoadingSpinner } from 'components/shared/LoadingSpinner'
import { Button } from 'components/ui/Button'
import { showToast } from 'components/ui/toaster'
import { useAuth } from 'contexts/AuthContext'
import { ConversationChatPanel } from 'modules/coaching/components/ConversationChatPanel'
import { ConversationProcessingView } from 'modules/coaching/components/ConversationProcessingView'
import styles from 'modules/coaching/components/VideoAgentScreen.module.css'
import { useAgentInteraction } from 'modules/coaching/hooks/useAgentInteraction'
import { usePendingFirstPromptState } from 'modules/coaching/hooks/usePendingFirstPromptState'
import { useInvalidateConversationMessages, useUpdateConversationSharingMutation } from 'modules/coaching/mutations'
import { useConversationMessagesQuery, useConversationQuery } from 'modules/coaching/queries'
import { getConversationFiles } from 'modules/coaching/utils/conversationFiles'
import {
  getBackgroundVideoUrls,
  getChatCompleteHandler,
  getConversationPlaybackState,
  getDisplayMessages,
  getDownloadFileName,
  getDownloadVideoUrl,
  getInitialUploadMessageIndex,
  getInputDisabled,
  getInputPlaceholder,
  getIsFollowUpUploadDisabled,
  getIsFollowUpUploadInProgress,
  getIsProcessingActive,
  getIsUploadLeaveProtectionEnabled,
  getPersistedVideoArtifactUrl,
  getProcessingState,
  getShouldRenderChatLoading,
  getShouldRenderConversationNotFound,
  getShouldRenderUploadFailure,
  hasNonVideoArtifactContent,
  isConversationFileFailed,
  isConversationFileProcessing,
  timeToSeconds,
  toUploadedVideoSource,
} from 'modules/coaching/utils/videoAgentScreenState'
import { useConversationVideoProcessingState } from 'modules/files/hooks/useConversationVideoProcessingState'
import { useFollowUpVideoUploadMutation } from 'modules/files/mutations'
import { webLogger } from 'utils/logger'
import { PERCENT_SCALE } from 'utils/units'
import { getVideoDuration } from 'utils/videoUtils'

import type { components } from '@startech/openapi'
import type { AgentMessage } from 'modules/coaching/types'
import type { UploadedVideoSource } from 'modules/coaching/utils/videoAgentScreenState'
import type { FormEvent, KeyboardEvent } from 'react'

interface VideoAgentScreenProperties {
  conversationId: string | undefined
}

interface EditableVideoAgentScreenProperties {
  conversationId: string
  isPublic: boolean
  viewerKey: string
}

type ConversationWithFile = components['schemas']['ConversationWithFile']

const autoResizeTextArea = (element: HTMLTextAreaElement) => {
  element.style.height = 'auto'
  element.style.height = `${element.scrollHeight}px`
}

const useChatCompleteHandler = (
  conversationId: string | undefined,
  invalidateConversationMessages: Parameters<typeof getChatCompleteHandler>[1]
) => {
  return useMemo(() => {
    return getChatCompleteHandler(conversationId, invalidateConversationMessages)
  }, [conversationId, invalidateConversationMessages])
}

const useUploadLeaveProtection = (isUploading: boolean) => {
  useLayoutEffect(() => {
    if (!isUploading) {
      return
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault()
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [isUploading])
}

const useMessagesEndReference = (messages: AgentMessage[]) => {
  const messagesEndReference = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    messagesEndReference.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  return messagesEndReference
}

const EditableVideoAgentScreen = ({ conversationId, isPublic, viewerKey }: EditableVideoAgentScreenProperties) => {
  /* --- Section 1: Hooks --- */
  const navigate = useNavigate()
  const videoReference = useRef<HTMLVideoElement | null>(null)
  const isFollowUpUploadStartingReference = useRef(false)
  const {
    data: conversationResponse,
    error: conversationError,
    isLoading: isConversationLoading,
  } = useConversationQuery(conversationId, viewerKey)
  const { data: persistedMessages = [], isLoading: isMessagesLoading } = useConversationMessagesQuery(
    conversationId,
    viewerKey
  )
  const {
    backgroundVideoUrls,
    handleRetry,
    hasPendingUploadForCurrentConversation,
    uploadError,
    uploadProgress,
    uploading,
  } = useConversationVideoProcessingState({
    conversationId,
  })
  const invalidateConversationMessages = useInvalidateConversationMessages()
  const updateSharing = useUpdateConversationSharingMutation()
  const followUpVideoUpload = useFollowUpVideoUploadMutation()
  const chatCompleteHandler = useChatCompleteHandler(conversationId, invalidateConversationMessages)
  const {
    canStop,
    error: agentError,
    isProcessing,
    isRestoring,
    isStopping,
    messages,
    sendMessage,
    stopResponse,
  } = useAgentInteraction({
    conversationId,
    initialMessages: persistedMessages,
    onChatComplete: chatCompleteHandler,
    viewerKey,
  })
  useUploadLeaveProtection(getIsUploadLeaveProtectionEnabled(uploading, followUpVideoUpload.isPending))
  const messagesEndReference = useMessagesEndReference(messages)

  const { pendingConfirmed, pendingPrompt, pendingPromptReference, setPendingConfirmed, setPendingPrompt } =
    usePendingFirstPromptState({
      conversationId,
      conversationFile: conversationResponse?.data.file,
      hasPendingUploadForCurrentConversation,
      isMessagesLoading,
      messages,
      sendMessage,
      uploading,
    })

  /* --- Section 2: Local State --- */
  const [inputValue, setInputValue] = useState('')
  const [followUpUploadError, setFollowUpUploadError] = useState<string | undefined>(undefined)
  const [followUpUploadProgress, setFollowUpUploadProgress] = useState<
    { percentage: number; phase: 'uploading' } | undefined
  >(undefined)

  /* --- Section 3: Derived Values --- */
  const conversation = conversationResponse?.data
  const persistedVideoArtifactUrl = useMemo(() => getPersistedVideoArtifactUrl(persistedMessages), [persistedMessages])

  const conversationFile = conversation?.file
  const conversationFileId = conversation?.file_id
  const conversationFiles = useMemo(() => getConversationFiles(conversation), [conversation])
  const isFileProcessing = isConversationFileProcessing(conversationFile)
  const isFileFailed = isConversationFileFailed(conversationFile)
  const isFollowUpUploadInProgress = getIsFollowUpUploadInProgress(
    followUpVideoUpload.isPending,
    followUpUploadProgress
  )

  const { muxPlayback, videoDuration, videoUrl } = getConversationPlaybackState({
    backgroundVideoUrl: backgroundVideoUrls[0],
    conversationFile,
    persistedVideoArtifactUrl,
  })
  const isProcessingActive = getIsProcessingActive({
    hasPendingUploadForCurrentConversation,
    isFileProcessing,
    uploading,
  })
  const inputDisabled = getInputDisabled(isProcessingActive, isFileFailed)
  const videoSources = useMemo<UploadedVideoSource[]>(() => {
    return conversationFiles.flatMap((file, index) => toUploadedVideoSource(file, index, conversationFileId))
  }, [conversationFileId, conversationFiles])

  const hasSentFirstQuestion = useMemo(() => {
    return messages.some(message => hasNonVideoArtifactContent(message))
  }, [messages])

  const { processingPanelPercentage, processingPanelStatusText, shouldRenderProcessingView } = getProcessingState({
    hasConversationFile: Boolean(conversationFile),
    hasSentFirstQuestion,
    isFileFailed,
    isMessagesLoading,
    isProcessingActive,
    uploadProgress,
  })

  const processingBackgroundVideoUrls = useMemo(() => {
    return getBackgroundVideoUrls({
      pendingPreviewUrls: backgroundVideoUrls,
      videoSources,
      fallbackVideoUrl: videoUrl,
    })
  }, [backgroundVideoUrls, videoSources, videoUrl])

  const displayMessages = useMemo<AgentMessage[]>(() => {
    return getDisplayMessages({
      conversationFileId,
      conversationId,
      messages,
      videoSources,
      videoUrl,
    })
  }, [conversationFileId, conversationId, messages, videoSources, videoUrl])

  const initialUploadMessageIndex = useMemo(() => {
    return getInitialUploadMessageIndex(displayMessages)
  }, [displayMessages])

  const downloadVideoUrl = getDownloadVideoUrl(conversationFile)
  const downloadFileName = useMemo(() => {
    return getDownloadFileName(conversation?.title)
  }, [conversation?.title])
  const shouldRenderUploadFailure = getShouldRenderUploadFailure({
    hasPendingUploadForCurrentConversation,
    uploadError,
    uploading,
  })
  const shouldRenderConversationNotFound = getShouldRenderConversationNotFound({
    conversation,
    conversationError,
    isConversationLoading,
  })
  const shouldRenderChatLoading = getShouldRenderChatLoading({
    authLoading: false,
    isConversationLoading,
    isMessagesLoading,
    videoUrl,
  })
  const attachedVideoCount = Math.max(conversationFiles.length, backgroundVideoUrls.length, 1)

  /* --- Section 4: Event Handlers --- */
  const handleTextAreaInput = useCallback((event: FormEvent<HTMLTextAreaElement>) => {
    autoResizeTextArea(event.currentTarget)
  }, [])

  const handleTextAreaEnterSubmit = useCallback((event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      event.currentTarget.form?.requestSubmit()
    }
  }, [])

  const handlePendingSubmit = useCallback(
    async (event?: FormEvent) => {
      event?.preventDefault()
      const prompt = pendingPrompt.trim()
      if (!prompt) {
        return
      }

      if (isProcessingActive || !conversationFile || isMessagesLoading) {
        pendingPromptReference.current = prompt
        setPendingConfirmed(true)
        return
      }

      setPendingConfirmed(false)
      pendingPromptReference.current = ''
      setPendingPrompt('')
      await sendMessage(prompt)
    },
    [
      conversationFile,
      isMessagesLoading,
      isProcessingActive,
      pendingPrompt,
      pendingPromptReference,
      sendMessage,
      setPendingConfirmed,
      setPendingPrompt,
    ]
  )

  const handleSendMessage = useCallback(
    async (event?: FormEvent) => {
      event?.preventDefault()
      if (!inputValue.trim() || isProcessing || inputDisabled) {
        return
      }

      const message = inputValue.trim()
      setInputValue('')
      await sendMessage(message)
    },
    [inputDisabled, inputValue, isProcessing, sendMessage]
  )

  const handleFollowUpVideoFilesDropped = useCallback(
    async (files: File[]) => {
      if (
        !conversationId ||
        files.length === 0 ||
        followUpVideoUpload.isPending ||
        isFollowUpUploadStartingReference.current
      ) {
        return
      }

      isFollowUpUploadStartingReference.current = true
      setFollowUpUploadError(undefined)
      setFollowUpUploadProgress({ percentage: 0, phase: 'uploading' })

      try {
        for (const [index, file] of Array.from(files.entries())) {
          const duration = await getVideoDuration(file)
          await followUpVideoUpload.mutateAsync({
            conversationId,
            duration: typeof duration === 'number' && duration > 0 ? duration : undefined,
            file,
            onProgress: (percentage, phase) => {
              const overall = Math.round(((index + percentage / PERCENT_SCALE) / files.length) * PERCENT_SCALE)
              setFollowUpUploadProgress({ percentage: overall, phase })
            },
          })
        }
        setFollowUpUploadProgress(undefined)
        showToast(
          files.length === 1 ? 'Video added to this conversation.' : 'Videos added to this conversation.',
          'success'
        )
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : 'Video upload failed'
        setFollowUpUploadError(message)
        setFollowUpUploadProgress(undefined)
        showToast(message, 'error')
      } finally {
        isFollowUpUploadStartingReference.current = false
      }
    },
    [conversationId, followUpVideoUpload]
  )

  const handleSharingToggle = useCallback(async () => {
    const nextIsPublic = !isPublic
    try {
      await updateSharing.mutateAsync({ conversationId, isPublic: nextIsPublic })
      if (!nextIsPublic) {
        showToast('Conversation is private.', 'success')
        return
      }

      const shareUrl = `${globalThis.location.origin}/c/${conversationId}`
      try {
        await navigator.clipboard.writeText(shareUrl)
        showToast('Public link copied.', 'success')
      } catch {
        showToast('Conversation is public. Copy the link from your browser.', 'warning')
      }
    } catch (error: unknown) {
      showToast(error instanceof Error ? error.message : 'Unable to update sharing.', 'error')
    }
  }, [conversationId, isPublic, updateSharing])

  const handleSeekToTime = useCallback((startTime: string) => {
    const video = videoReference.current
    if (!video) {
      return
    }

    video.currentTime = timeToSeconds(startTime)
    video.play().catch((error: unknown) => {
      webLogger.warn('Failed to autoplay selected video segment', {
        error: error instanceof Error ? error.message : String(error),
      })
    })
  }, [])

  const handlePlaySegment = useCallback((startTime: string) => {
    const video = videoReference.current
    if (!video) {
      return
    }

    video.currentTime = timeToSeconds(startTime)
    video.play().catch((error: unknown) => {
      webLogger.warn('Failed to autoplay inline video segment', {
        error: error instanceof Error ? error.message : String(error),
      })
    })
  }, [])

  /* --- Section 5: Early Returns --- */
  if (shouldRenderUploadFailure) {
    return (
      <div className={styles.videoAgentPage}>
        <div className={styles.videoAgentErrorContainer}>
          <div className={styles.videoAgentError}>
            <h2>Upload Failed</h2>
            <p>{uploadError}</p>
            <div className={styles.videoAgentErrorActions}>
              <Button className={styles.videoAgentRetryButton} onClick={handleRetry} variant="primary">
                Try Again
              </Button>
              <Button className={styles.videoAgentSecondaryButton} onClick={() => navigate('/new')} variant="secondary">
                Upload Different Video
              </Button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (shouldRenderProcessingView) {
    return (
      <ConversationProcessingView
        backgroundVideoUrls={processingBackgroundVideoUrls}
        isPendingConfirmed={pendingConfirmed}
        pendingPrompt={pendingPrompt}
        processingPanelPercentage={processingPanelPercentage}
        processingPanelStatusText={processingPanelStatusText}
        uploadError={uploadError}
        videoCount={attachedVideoCount}
        videoUrl={videoUrl}
        onCancelPendingPrompt={() => {
          setPendingConfirmed(false)
          pendingPromptReference.current = ''
        }}
        onPendingPromptChange={setPendingPrompt}
        onPendingPromptKeyDown={handleTextAreaEnterSubmit}
        onPendingPromptSubmit={handlePendingSubmit}
        onPendingPromptTextArea={handleTextAreaInput}
      />
    )
  }

  if (shouldRenderConversationNotFound) {
    return (
      <div className={styles.videoAgentPage}>
        <div className={styles.videoAgentErrorContainer}>
          <div className={styles.videoAgentError}>
            <h2>Conversation Not Found</h2>
            <p>This conversation doesn&apos;t exist or you don&apos;t have access to it.</p>
            <Button className={styles.videoAgentRetryButton} onClick={() => navigate('/new')} variant="primary">
              Upload a Video
            </Button>
          </div>
        </div>
      </div>
    )
  }

  if (shouldRenderChatLoading) {
    return (
      <div className={styles.videoAgentPage}>
        <div className={styles.videoAgentLoadingContainer}>
          <LoadingSpinner />
        </div>
      </div>
    )
  }

  /* --- Section 6: Render --- */
  return (
    <div className={styles.videoAgentPage}>
      <div className={styles.videoAgentShareAction}>
        <Button disabled={updateSharing.isPending} onClick={handleSharingToggle} variant="secondary">
          {isPublic ? 'Make private' : 'Share'}
        </Button>
      </div>
      <ConversationChatPanel
        agentError={agentError}
        displayMessages={displayMessages}
        downloadFileName={downloadFileName}
        downloadVideoUrl={downloadVideoUrl}
        initialUploadMessageIndex={initialUploadMessageIndex}
        inputPlaceholder={getInputPlaceholder(isFileFailed, isFileProcessing)}
        inputValue={inputValue}
        attachedVideoCount={conversationFiles.length}
        isFollowUpUploadDisabled={getIsFollowUpUploadDisabled(inputDisabled, isFollowUpUploadInProgress)}
        isInputDisabled={inputDisabled}
        isProcessing={isProcessing}
        isRestoring={isRestoring}
        isStopping={isStopping}
        isStopVisible={canStop}
        isUploadingFollowUpVideo={isFollowUpUploadInProgress}
        messagesEndReference={messagesEndReference}
        mux={muxPlayback}
        followUpUploadError={followUpUploadError}
        followUpUploadProgress={followUpUploadProgress}
        onFollowUpVideoFilesDropped={handleFollowUpVideoFilesDropped}
        onInputChange={setInputValue}
        onInputKeyDown={handleTextAreaEnterSubmit}
        onInputSubmit={handleSendMessage}
        onInputTextArea={handleTextAreaInput}
        onStopResponse={stopResponse}
        onPlaySegment={handlePlaySegment}
        onSeekToTime={handleSeekToTime}
        videoDuration={videoDuration}
        videoReference={videoReference}
        videoSources={videoSources}
        videoUrl={videoUrl}
      />
    </div>
  )
}

const ReadOnlyVideoAgentScreen = ({
  conversation,
  conversationId,
  messages,
}: {
  conversation: ConversationWithFile
  conversationId: string
  messages: AgentMessage[]
}) => {
  /* --- Section 1: Hooks --- */
  const videoReference = useRef<HTMLVideoElement | null>(null)
  const messagesEndReference = useMessagesEndReference(messages)

  /* --- Section 3: Derived Values --- */
  const persistedVideoArtifactUrl = useMemo(() => getPersistedVideoArtifactUrl(messages), [messages])
  const conversationFile = conversation.file
  const conversationFileId = conversation.file_id
  const conversationFiles = useMemo(() => getConversationFiles(conversation), [conversation])
  const { muxPlayback, videoDuration, videoUrl } = getConversationPlaybackState({
    backgroundVideoUrl: undefined,
    conversationFile,
    persistedVideoArtifactUrl,
  })
  const videoSources = useMemo<UploadedVideoSource[]>(() => {
    return conversationFiles.flatMap((file, index) => toUploadedVideoSource(file, index, conversationFileId))
  }, [conversationFileId, conversationFiles])
  const displayMessages = useMemo<AgentMessage[]>(() => {
    return getDisplayMessages({
      conversationFileId,
      conversationId,
      messages,
      videoSources,
      videoUrl,
    })
  }, [conversationFileId, conversationId, messages, videoSources, videoUrl])
  const initialUploadMessageIndex = useMemo(() => getInitialUploadMessageIndex(displayMessages), [displayMessages])

  /* --- Section 4: Event Handlers --- */
  const handleSeekToTime = useCallback((startTime: string) => {
    const video = videoReference.current
    if (!video) {
      return
    }
    video.currentTime = timeToSeconds(startTime)
    video.play().catch((error: unknown) => {
      webLogger.warn('Failed to autoplay selected video segment', {
        error: error instanceof Error ? error.message : String(error),
      })
    })
  }, [])
  const handlePlaySegment = useCallback((startTime: string) => {
    const video = videoReference.current
    if (!video) {
      return
    }
    video.currentTime = timeToSeconds(startTime)
    video.play().catch((error: unknown) => {
      webLogger.warn('Failed to autoplay inline video segment', {
        error: error instanceof Error ? error.message : String(error),
      })
    })
  }, [])

  /* --- Section 6: Render --- */
  return (
    <div className={styles.videoAgentPage}>
      <ConversationChatPanel
        agentError={undefined}
        displayMessages={displayMessages}
        downloadFileName={undefined}
        downloadVideoUrl={undefined}
        initialUploadMessageIndex={initialUploadMessageIndex}
        isReadOnly
        messagesEndReference={messagesEndReference}
        mux={muxPlayback}
        onPlaySegment={handlePlaySegment}
        onSeekToTime={handleSeekToTime}
        videoDuration={videoDuration}
        videoReference={videoReference}
        videoSources={videoSources}
        videoUrl={videoUrl}
      />
    </div>
  )
}

export const VideoAgentScreen = ({ conversationId }: VideoAgentScreenProperties) => {
  /* --- Section 1: Hooks --- */
  const navigate = useNavigate()
  const { loading: authLoading, user } = useAuth()
  const {
    data: conversationResponse,
    error: conversationError,
    isLoading: isConversationLoading,
  } = useConversationQuery(conversationId, user?.id ?? 'anonymous', !authLoading)
  const {
    data: messages = [],
    error: messagesError,
    isLoading: isMessagesLoading,
  } = useConversationMessagesQuery(conversationId, user?.id ?? 'anonymous', !authLoading)

  /* --- Section 3: Derived Values --- */
  const viewerKey = user?.id ?? 'anonymous'

  /* --- Section 5: Early Returns --- */
  if (!conversationId) {
    return (
      <div className={styles.videoAgentPage}>
        <div className={styles.videoAgentErrorContainer}>
          <div className={styles.videoAgentError}>
            <h2>Upload not found</h2>
            <p>Please start a new upload.</p>
            <Button className={styles.videoAgentRetryButton} onClick={() => navigate('/new')} variant="primary">
              Upload a Video
            </Button>
          </div>
        </div>
      </div>
    )
  }

  if (authLoading || isConversationLoading || isMessagesLoading) {
    return (
      <div className={styles.videoAgentPage}>
        <div className={styles.videoAgentLoadingContainer}>
          <LoadingSpinner />
        </div>
      </div>
    )
  }

  if (!conversationResponse || conversationError || messagesError) {
    return (
      <div className={styles.videoAgentPage}>
        <div className={styles.videoAgentErrorContainer}>
          <div className={styles.videoAgentError}>
            <h2>Conversation Not Found</h2>
            <p>This conversation doesn&apos;t exist or you don&apos;t have access to it.</p>
            <Button className={styles.videoAgentRetryButton} onClick={() => navigate('/new')} variant="primary">
              Upload a Video
            </Button>
          </div>
        </div>
      </div>
    )
  }

  /* --- Section 6: Render --- */
  return conversationResponse.access.isOwner ? (
    <EditableVideoAgentScreen
      conversationId={conversationId}
      isPublic={conversationResponse.access.isPublic}
      viewerKey={viewerKey}
    />
  ) : (
    <ReadOnlyVideoAgentScreen
      conversation={conversationResponse.data}
      conversationId={conversationId}
      messages={messages}
    />
  )
}
